/**
 * OnboardingChecklist — B-2026-0078
 *
 * A persistent but dismissible 3-step checklist widget rendered on
 * AthleteDashboard when onboarding_complete = false.
 *
 * Steps:
 *   1. Join a channel    — checked when has_joined_channel
 *   2. Pick a template   — CTA navigates to joined channel template list
 *   3. Submit first eval — CTA navigates to /evaluate
 *
 * Dismissal: stored in localStorage under ONBOARDING_DISMISSED_KEY.
 * After the first eval is submitted the widget disappears automatically
 * (onboarding_complete becomes true on the next poll).
 *
 * Zero-template handling: step 2 shows a friendly message instead of a
 * broken CTA when the channel has no finalized templates.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import type { OnboardingStatus } from '@/services/onboardingApi';
import type { Channel } from '@/types/channel';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const ONBOARDING_DISMISSED_KEY = 'sf_onboarding_checklist_dismissed_at';

/**
 * Returns true if the user dismissed the checklist more than 2 days ago.
 * Used to re-show the widget if the user hasn't completed onboarding yet.
 */
function isDismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(ONBOARDING_DISMISSED_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (isNaN(ts)) return false;
    const twoDaysMs = 2 * 24 * 60 * 60 * 1000;
    return Date.now() - ts < twoDaysMs;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// StepRow
// ---------------------------------------------------------------------------

interface StepRowProps {
  index: number;
  label: string;
  done: boolean;
  children?: React.ReactNode;
}

function StepRow({ index, label, done, children }: StepRowProps) {
  const accent = done ? 'var(--color-accent)' : 'var(--color-accent-blue)';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '10px 0',
        borderBottom: index < 2 ? '1px solid var(--color-border-subtle)' : 'none',
        opacity: done ? 0.65 : 1,
        transition: 'opacity 200ms ease',
      }}
    >
      {/* Step indicator */}
      <div style={{ flexShrink: 0, paddingTop: '1px' }}>
        {done ? (
          <CheckCircleIconSolid
            style={{ width: '20px', height: '20px', color: 'var(--color-accent)' }}
          />
        ) : (
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: `2px solid ${accent}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 700,
              color: accent,
            }}
          >
            {index + 1}
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontSize: '13px',
            fontWeight: done ? 400 : 600,
            color: done ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
            margin: 0,
            textDecoration: done ? 'line-through' : 'none',
          }}
        >
          {label}
        </p>
        {!done && children && (
          <div style={{ marginTop: '6px' }}>{children}</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface OnboardingChecklistProps {
  status: OnboardingStatus;
  /** The user's joined channels — used to link to the first channel's template list. */
  channels: Channel[];
}

export function OnboardingChecklist({ status, channels }: OnboardingChecklistProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<boolean>(isDismissedRecently);

  // Don't render when complete or dismissed.
  if (status.onboarding_complete || dismissed) return null;

  const firstChannel = channels[0];
  const hasChannel = channels.length > 0;

  function handleDismiss() {
    try {
      localStorage.setItem(ONBOARDING_DISMISSED_KEY, String(Date.now()));
    } catch {
      // localStorage unavailable — dismiss in-memory only
    }
    setDismissed(true);
  }

  // Step 2 CTA: navigate to first channel if available
  function handlePickTemplate() {
    if (firstChannel) {
      navigate(`/channels/${firstChannel.slug}`);
    } else {
      navigate('/channels');
    }
  }

  return (
    <div
      data-testid="onboarding-checklist"
      style={{
        backgroundColor: 'var(--color-dark-card)',
        border: '1px solid rgba(61, 169, 252, 0.25)',
        borderRadius: '16px',
        padding: '20px 22px',
        boxShadow: '0 0 24px rgba(61, 169, 252, 0.08)',
        position: 'relative',
        marginBottom: '20px',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '9px',
              backgroundColor: 'rgba(61, 169, 252, 0.12)',
              border: '1px solid rgba(61, 169, 252, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <SparklesIcon
              style={{ width: '16px', height: '16px', color: 'var(--color-accent-blue)' }}
            />
          </div>
          <div>
            <p
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: '14px',
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              Get your first AI score in 5 minutes
            </p>
            <p
              style={{
                fontSize: '12px',
                color: 'var(--color-text-muted)',
                margin: '2px 0 0',
              }}
            >
              3 steps to see your movement analysed by AI
            </p>
          </div>
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss onboarding checklist"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px',
            color: 'var(--color-text-muted)',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <XMarkIcon style={{ width: '16px', height: '16px' }} />
        </button>
      </div>

      {/* Steps */}
      <div>
        {/* Step 1 — Join a channel */}
        <StepRow index={0} label="Join a channel" done={status.has_joined_channel}>
          <Link
            to="/channels"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--color-accent-blue)',
              textDecoration: 'none',
            }}
          >
            Browse channels
          </Link>
        </StepRow>

        {/* Step 2 — Pick a template */}
        <StepRow
          index={1}
          label="Pick a movement template"
          done={status.has_selected_template}
        >
          {hasChannel ? (
            <button
              onClick={handlePickTemplate}
              data-testid="onboarding-pick-template-cta"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--color-accent-blue)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              View templates in {firstChannel?.display_name ?? 'your channel'}
            </button>
          ) : (
            <p
              data-testid="onboarding-no-templates-msg"
              style={{ fontSize: '12px', color: 'var(--color-text-muted)', margin: 0 }}
            >
              Your coach hasn&apos;t added templates yet — check back soon!
            </p>
          )}
        </StepRow>

        {/* Step 3 — Submit first evaluation */}
        <StepRow
          index={2}
          label="Submit your first evaluation"
          done={status.has_submitted_eval}
        >
          <Link
            to="/evaluate"
            data-testid="onboarding-submit-eval-cta"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--color-accent-blue)',
              textDecoration: 'none',
            }}
          >
            Upload a video and get scored
          </Link>
        </StepRow>
      </div>

      {/* Progress indicator */}
      {(() => {
        const done = [
          status.has_joined_channel,
          status.has_selected_template,
          status.has_submitted_eval,
        ].filter(Boolean).length;
        return (
          <div
            style={{
              marginTop: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <div
              style={{
                flex: 1,
                height: '4px',
                borderRadius: '2px',
                backgroundColor: 'var(--color-dark-elevated-2)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.round((done / 3) * 100)}%`,
                  backgroundColor: 'var(--color-accent-blue)',
                  borderRadius: '2px',
                  transition: 'width 400ms ease',
                }}
              />
            </div>
            <span
              style={{ fontSize: '11px', color: 'var(--color-text-muted)', flexShrink: 0 }}
            >
              {done}/3
            </span>
          </div>
        );
      })()}
    </div>
  );
}
